<script setup>
import moment from 'moment'
import { useUserGroupsStore, useUsersStore } from '@/stores'
import { numberWithCommas } from '@/plugins/misc'
import { useDebounce } from '@vueuse/core'
import CommsHistoryPanel from '@core/components/CommsHistoryPanel.vue'

const userGroupsStore = useUserGroupsStore()
let userGroups = null
let editingUserGroup = null
let fetching = null;
({  userGroups, editingUserGroup, fetching  } = storeToRefs(userGroupsStore))

const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const userGroupsPerPage = 40

const searchValue = ref("")
const debouncedSearchText = useDebounce(searchValue, 500)

const filtering = ref(false)
const currentPage = ref(1)
const orderBy = ref("name")
const orderDirection = ref("") // this should be "" or "-" for desc
const groupTypeFiltering = ref("enterprise")
const subscriptionStatusFiltering = ref("")

const isConfirmDialogOpen = ref(false)
const selectedUserGroup = ref(null)

// Fetch groups from the lightweight list endpoint with server-side filtering
const fetchGroups = () => {
  const params = {}
  if (groupTypeFiltering.value) params.type = groupTypeFiltering.value
  if (debouncedSearchText.value && debouncedSearchText.value.length > 1) {
    params.searchTerm = debouncedSearchText.value
  }
  if (orderBy.value) params.sortBy = orderBy.value
  if (orderDirection.value === '-') params.sortDir = 'desc'
  userGroupsStore.getGroupsList(params)
}

// Initial load
fetchGroups()

// Re-fetch when filters change (server-side filtering)
watch(groupTypeFiltering, () => {
  currentPage.value = 1
  fetchGroups()
})

watch(debouncedSearchText, newVal => {
  currentPage.value = 1
  if (!newVal || newVal.length > 1) {
    fetchGroups()
  }
})

watch(orderBy, () => fetchGroups())
watch(orderDirection, () => fetchGroups())

const dynamicSort = function(property) {
  var sortOrder = 1
  if(property[0] === "-") {
    sortOrder = -1
    property = property.substr(1)
  }

  return function (a,b) {
    let x = a[property] ? a[property] : ""
    let y = b[property] ? b[property] : ""

    var result = (x < y) ? -1 : (x > y) ? 1 : 0

    return result * sortOrder
  }
}

const filterTypes = [
  {text:'Show all', value: ''},
  {text:'Platinum', value: 'enterprise'},
  {text:'Discount', value: 'discount'},
  {text:'Friends', value: 'friends'},
  {text:'Restaurant Group', value: 'restaurantGroup'}
]

const filteredUserGroups = computed(() => {
  let returnArray = [...userGroups.value]

  // Subscription status filter is still client-side since the field is virtual
  if(subscriptionStatusFiltering.value && subscriptionStatusFiltering.value != ''){
    returnArray = returnArray.filter(item => {
      return item.platinumMembershipStatus == subscriptionStatusFiltering.value
    })
  }

  return returnArray
})

// Comms panel: track which group's comms history is expanded.
const commsGroupId = ref('')

const groupOptions = computed(() => {
  return (filteredUserGroups.value || []).map(g => ({
    label: `${g.name || '(no name)'} — ${g.type || 'group'}`,
    value: g._id,
  }))
})
</script>

<template>
  <div>
    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <ConfirmDialog
        v-model:is-dialog-visible="isConfirmDialogOpen"
        confirmation-msg="Are you sure you want to delete this group? This is not reversible!"
        @confirm="userGroupsStore.adminDeleteUserGroup(selectedUserGroup._id)"
      />
      <div>
        <VCard class="mb-4 pa-4 pt-8 pb-7">
          <div class="account-card">
            <div class="account-card-content">
              <div class="mb-2">
                Filters
              </div>
              <VSelect
                v-model="groupTypeFiltering"
                class="mb-4"
                label="Type"
                item-title="text"
                item-value="value"
                :items="filterTypes"
                required
              />
              <VSelect
                v-model="subscriptionStatusFiltering"
                class="mb-4"
                label="Subscription Status"
                item-title="text"
                item-value="value"
                :items="[{text:'Show all', value: ''},{text:'Current', value: 'current'},{text:'Past Due', value: 'pastDue'},{text:'Pending', value: 'pending'},{text:'Canceled', value: 'canceled'},{text:'Expired', value: 'expired'},{text:'No Membership', value: 'none'}]"
                required
              />
              <VDivider class="mb-4" />
              <div class="mb-2">
                Sorting
              </div>
              <VSelect
                v-model="orderBy"
                class="mb-2"
                label="Order By"
                item-title="text"
                item-value="value"
                :items="[{text:'Name', value: 'name'}, {text:'Renewal Date', value: 'premiumRenewalDate'}]"

                required
              />
              <VRadioGroup
                v-model="orderDirection"
                row
              >
                <VRadio
                  label="▲ Ascending"
                  value=""
                  density="compact"
                />
                <VRadio
                  label="▼ Descending"
                  value="-"
                  density="compact"
                />
              </VRadioGroup>

              <VBtn
                class="account-card-primary-action-button"
                @click="userGroupsStore.adminNewUserGroup()"
              >
                <VIcon

                  start
                  size="18"
                  icon="mdi-add-circle-outline"
                />
                <span>New Group</span>
              </VBtn>
            </div>
          </div>
        </VCard>
      </div>
      <div>
        <VCard class="pa-10 pt-8">
          <h3 class="user-dashboard-card-title mb-1">
            {{ ( groupTypeFiltering =="enterprise" ? 'Platinum' : groupTypeFiltering.charAt(0).toUpperCase() + groupTypeFiltering.slice(1)) }} ({{ filteredUserGroups.length }})
          </h3>

          <VTextField
            v-model="searchValue"
            :loading="fetching || filtering"
            density="compact"
            variant="solo"
            :label="(fetching ? 'Loading... Please wait' : 'Search by name etc.')"
            label="Search Groups"
            append-inner-icon="mdi-magnify"
            single-line
            hide-details
            class="mb-4"
            @keyup="currentPage = 1"
          />
          <div>
            <template v-for="(userGroup, index) in filteredUserGroups">
              <VCard
                v-if="((index >= (currentPage*userGroupsPerPage)-userGroupsPerPage) && (index <= currentPage*userGroupsPerPage))"
                :key="userGroup.name"
                class="mb-4"
                @click="userGroupsStore.adminEditUserGroup(userGroup._id)"
              >
                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      density="compact"
                      class="admin-group-menu-button"
                      icon="mdi-dots-vertical"
                      v-bind="props"
                    />
                  </template>

                  <VList>
                    <VListItem
                      v-for="(item, i) in [
                        { title: 'Edit', action: function (){userGroupsStore.adminEditUserGroup(userGroup._id)}},
                        { title: 'Membership', action: function (){userGroupsStore.adminEditUserGroupSubscription(userGroup._id)}},
                        { title: 'Members', action: function (){userGroupsStore.adminEditUserGroupMembers(userGroup._id)}},
                        { title: 'Delete', action: function (){ isConfirmDialogOpen = true }} ]"
                      v-if="userGroup.type == 'enterprise'"
                      :key="i"
                      @click="selectedUserGroup = userGroup; item.action();"
                    >
                      <VListItemTitle>{{ item.title }}</VListItemTitle>
                    </VListItem>

                    <VListItem
                      v-for="(item, j) in [
                        { title: 'Edit', action: function (){userGroupsStore.adminEditUserGroup(userGroup._id)}},
                        { title: 'Members', action: function (){userGroupsStore.adminEditUserGroupMembers(userGroup._id)}},
                        ...(userGroup.type === 'discount' ? [{ title: 'Clone', action: function (){ userGroupsStore.adminCloneUserGroup(userGroup._id) }}] : []),
                        { title: 'Delete', action: function (){ isConfirmDialogOpen = true }} ]"
                      v-else
                      :key="j"
                      @click="selectedUserGroup = userGroup; item.action();"
                    >
                      <VListItemTitle>{{ item.title }}</VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>

                <VCardItem class="pb-0">
                  <div v-if="userGroup.name">
                    <b>{{ userGroup.name }}</b>
                  </div>
                  <div v-else>
                    <i>No name</i>
                  </div>
                </VCardItem>

                <VCardText class="pt-0">
                  <!--                    <div v-if="userGroup.hasOwnProperty('pricePerSeatPerBillingPeriod') && typeof userGroup.pricePerSeatPerBillingPeriod == 'number'">{{userGroup.numberOfSeats}} seats @ ${{userGroup.pricePerSeatPerBillingPeriod}}/seat/yr</div> -->
                  <!--                    <div v-if="userGroup.hasOwnProperty('pricePerSeatPerBillingPeriod')  && typeof userGroup.pricePerSeatPerBillingPeriod == 'number'">Annual Spend: ${{numberWithCommas(userGroup.pricePerSeatPerBillingPeriod*userGroup.numberOfSeats)}}</div> -->


                  <div v-if="userGroup?.membershipPriceIndividualAnnual?.toString()">
                    Individual Rate: ${{ userGroup.membershipPriceIndividualAnnual }}/yr
                  </div>
                  <div v-if="userGroup?.platinumMembershipStatus">
                    Platinum Membership: {{ userGroup.platinumMembershipStatus }}
                  </div>
                  <div v-if="userGroup?.currentNumberOfPaidSeats">
                    Paid Memberships: {{ userGroup.currentNumberOfPaidSeats }}
                  </div>
                  <div v-if="userGroup?.memberCount || userGroup?.userPermissions?.length">
                    Total members: {{ userGroup.memberCount ?? userGroup.userPermissions?.length }}
                  </div>
                  <div v-if="userGroup?.platinumRenewalDate">
                    Renewal Date: {{ userGroup.platinumRenewalDate ? moment(userGroup.platinumRenewalDate).format("M/D/YY") : "none" }}
                  </div>
                  <div v-if="userGroup?.platinumExpiredOn">
                    Expired On: {{ userGroup.platinumExpiredOn }}
                  </div>
                  <div v-if="userGroup?.planIdToGive">
                    Trial Enabled
                  </div>
                </VCardText>
              </VCard>
            </template>

            <VPagination
              v-model="currentPage"
              :length="Math.ceil(filteredUserGroups.length / userGroupsPerPage)"
            />
          </div>

          <!-- Comms history panel: admin view for a selected group. -->
          <div
            v-if="myUser?.userLevel === 'admin'"
            class="mt-4"
          >
            <div class="text-subtitle-2 mb-2">
              Comms History — Group
            </div>
            <VSelect
              v-model="commsGroupId"
              :items="groupOptions"
              item-title="label"
              item-value="value"
              label="Select a group from the current view"
              density="compact"
              clearable
              hide-details
              class="mb-3"
              data-testid="comms-group-select"
            />
            <VTextField
              v-model="commsGroupId"
              label="Or paste a group ID"
              placeholder="Paste group _id"
              density="compact"
              clearable
              hide-details
              class="mb-3"
            />
            <CommsHistoryPanel
              v-if="commsGroupId"
              :group-id="commsGroupId"
            />
            <div
              v-else
              class="text-caption text-medium-emphasis"
            >
              Select or paste a group ID to load its comms.
            </div>
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style lang="css">
/* Write your own CSS for pagination */
.pagination .item {
  margin: 10px;
}

.admin-group-menu-button {
  position: absolute;
  float: right;
  top: 0;
  right: 0;
  margin: 20px;
}

.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
